import { createPayloadAction, RequestAction, createRequestActionCreators } from 'helpers/actionsHelpers'
import { PayloadAction } from 'interfaces/action'
import { CategoryId, TypedMoneyflowCategory } from 'interfaces/meta'
import {
  MoneyflowBreakdownCollection,
  MoneyflowTrackersCollection,
  MoneyflowTracker,
  MoneyflowType,
  MoneyflowTyped,
  BaseTransactionsPayload,
  TransactionsPayload,
  Transaction,
  FullMoneyflowType,
  TransactionUid,
} from 'interfaces/moneyflow'
import { Timeframe } from 'interfaces/chart'
import { PeergroupsTyped, MoneyflowPeergroupBreakdownCollection } from 'interfaces/peergroups'
import { PeergroupTrendCollection, Trend } from 'interfaces/trend'
import { Collection } from 'interfaces'

type SetVisibilityActionPayload = {
  type: MoneyflowType
  isVisible: boolean
}

type GetMoneyflowTrendActionPayload = {
  type: MoneyflowType
  timeframe: Timeframe
  category: CategoryId
}

type GetPeergroupMoneyflowTrendActionPayload = GetMoneyflowTrendActionPayload & PeergroupsTyped

interface MoneyflowBreakdownPayload<TType> extends MoneyflowTyped {
  timeframe: Timeframe
  data: TType
}

interface MoneyflowTrendPayload<TType> extends MoneyflowTyped {
  timeframe: Timeframe
  category: CategoryId
  data: TType
}

interface MoneyflowTrackerPayload<TType> extends MoneyflowTyped {
  data: TType
}

type TrackerActionPayload = {
  type: MoneyflowType
  categoryId: CategoryId
}

export type TransactionPayload = {
  type: FullMoneyflowType | null
  transaction: Transaction
  category: TypedMoneyflowCategory
  apply_to_ids: TransactionUid[]
  create_rule: boolean
}


// ===========================================================================//
//                                 BREAKDOWNS                                 //
// ===========================================================================//

type GetMoneyflowBreakdownPayload = {
  type: MoneyflowType
  timeframe: Timeframe
}


export const GET_MEMBER_MONEYFLOW_BREAKDOWN = 'GET_MEMBER_MONEYFLOW_BREAKDOWN' as const
type GetMemberMoneyflowBreakdown = PayloadAction<typeof GET_MEMBER_MONEYFLOW_BREAKDOWN, GetMoneyflowBreakdownPayload>
export const getMemberMoneyflowBreakdownActionCreator = (type: MoneyflowType, timeframe: Timeframe): GetMemberMoneyflowBreakdown =>
  createPayloadAction(GET_MEMBER_MONEYFLOW_BREAKDOWN, { type, timeframe })

export const SET_MEMBER_MONEYFLOW_BREAKDOWN = 'SET_MEMBER_MONEYFLOW_BREAKDOWN' as const
type SetMemberMoneyflowBreakdown = PayloadAction<typeof SET_MEMBER_MONEYFLOW_BREAKDOWN, MoneyflowBreakdownPayload<MoneyflowBreakdownCollection>>
export const setMemberMoneyflowBreakdownActionCreator =
  (type: MoneyflowType, timeframe: Timeframe, data: MoneyflowBreakdownCollection): SetMemberMoneyflowBreakdown =>
    createPayloadAction(SET_MEMBER_MONEYFLOW_BREAKDOWN, { type, timeframe, data })

export const GET_PEER_GROUP_MONEYFLOW_BREAKDOWN = 'GET_PEER_GROUP_MONEYFLOW_BREAKDOWN' as const
type GetPeergroupMoneyflowBreakdown = PayloadAction<typeof GET_PEER_GROUP_MONEYFLOW_BREAKDOWN, GetMoneyflowBreakdownPayload & PeergroupsTyped>
export const getPeergroupMoneyflowBreakdownActionCreator = (type: MoneyflowType, timeframe: Timeframe, peergroups: number[]): GetPeergroupMoneyflowBreakdown =>
  createPayloadAction(GET_PEER_GROUP_MONEYFLOW_BREAKDOWN, { type, timeframe, peergroups })

export const APPEND_PEER_GROUP_MONEYFLOW_BREAKDOWN = 'APPEND_PEER_GROUP_MONEYFLOW_BREAKDOWN' as const
type AppendPeergroupMoneyflowBreakdown = PayloadAction<typeof APPEND_PEER_GROUP_MONEYFLOW_BREAKDOWN, MoneyflowBreakdownPayload<MoneyflowPeergroupBreakdownCollection>>
export const appendPeergroupMoneyflowBreakdownActionCreator =
  (type: MoneyflowType, timeframe: Timeframe, data: MoneyflowPeergroupBreakdownCollection): AppendPeergroupMoneyflowBreakdown =>
    createPayloadAction(APPEND_PEER_GROUP_MONEYFLOW_BREAKDOWN, { type, timeframe, data })

export const SET_BREAKDOWN_VISIBILITY = 'SET_BREAKDOWN_VISIBILITY' as const
type SetBreakdownVisibility = PayloadAction<typeof SET_BREAKDOWN_VISIBILITY, SetVisibilityActionPayload>
export const setBreakdownVisibilityActionCreator = (type: MoneyflowType, isVisible: boolean): SetBreakdownVisibility =>
  createPayloadAction(SET_BREAKDOWN_VISIBILITY, { type, isVisible })

// ===========================================================================//
//                                   TRENDS                                   //
// ===========================================================================//

export const GET_MEMBER_MONEYFLOW_TRENDS = 'GET_MEMBER_MONEYFLOW_TRENDS' as const
type GetMemberMoneyflowTrend = PayloadAction<typeof GET_MEMBER_MONEYFLOW_TRENDS, GetMoneyflowTrendActionPayload>
export const getMemberMoneyflowTrendsActionCreator = (type: MoneyflowType, timeframe: Timeframe, category: CategoryId): GetMemberMoneyflowTrend =>
  createPayloadAction(GET_MEMBER_MONEYFLOW_TRENDS, { type, timeframe, category })

export const SET_MEMBER_MONEYFLOW_TRENDS = 'SET_MEMBER_MONEYFLOW_TRENDS' as const
type SetMemberMoneyflowTrend = PayloadAction<typeof SET_MEMBER_MONEYFLOW_TRENDS, MoneyflowTrendPayload<Trend>>
export const setMemberMoneyflowTrendsActionCreator =
  (type: MoneyflowType, timeframe: Timeframe, category: CategoryId, data: Trend): SetMemberMoneyflowTrend =>
    createPayloadAction(SET_MEMBER_MONEYFLOW_TRENDS, { type, timeframe, category, data })

export const GET_PEER_GROUP_MONEYFLOW_TRENDS = 'GET_PEER_GROUP_MONEYFLOW_TRENDS' as const
type GetPeergroupMoneyflowTrend = PayloadAction<typeof GET_PEER_GROUP_MONEYFLOW_TRENDS, GetPeergroupMoneyflowTrendActionPayload>
export const getPeergroupMoneyflowTrendsActionCreator =
  (type: MoneyflowType, timeframe: Timeframe, category: CategoryId, peergroups: number[]): GetPeergroupMoneyflowTrend =>
    createPayloadAction(GET_PEER_GROUP_MONEYFLOW_TRENDS, { type, timeframe, category, peergroups })

export const APPEND_PEER_GROUP_MONEYFLOW_TRENDS = 'APPEND_PEER_GROUP_MONEYFLOW_TRENDS' as const
type AppendPeergroupMoneyflowTrend = PayloadAction<typeof APPEND_PEER_GROUP_MONEYFLOW_TRENDS, MoneyflowTrendPayload<PeergroupTrendCollection>>
export const appendPeergroupMoneyflowTrendsActionCreator =
  (type: MoneyflowType, timeframe: Timeframe, category: CategoryId, data: PeergroupTrendCollection): AppendPeergroupMoneyflowTrend =>
    createPayloadAction(APPEND_PEER_GROUP_MONEYFLOW_TRENDS, { type, timeframe, category, data })


// ===========================================================================//
//                                    CATEGORIES                              //
// ===========================================================================//

export const SET_CATEGORIES_VISIBILITY = 'SET_CATEGORIES_VISIBILITY' as const
type SetCategoriesVisibility = PayloadAction<typeof SET_CATEGORIES_VISIBILITY, SetVisibilityActionPayload>
export const setCategoriesVisibilityActionCreator = (type: MoneyflowType, isVisible: boolean): SetCategoriesVisibility =>
  createPayloadAction(SET_CATEGORIES_VISIBILITY, { type, isVisible })


// ===========================================================================//
//                                     TRACKERS                               //
// ===========================================================================//

export const GET_MONEYFLOW_TRACKERS = 'GET_MONEYFLOW_TRACKERS' as const
type GetMoneyflowTrackers = RequestAction<typeof GET_MONEYFLOW_TRACKERS, MoneyflowTyped, MoneyflowTrackersCollection>
export const getMoneyflowTrackersActionCreator =
  createRequestActionCreators<typeof GET_MONEYFLOW_TRACKERS, MoneyflowTyped, MoneyflowTrackersCollection>(GET_MONEYFLOW_TRACKERS)

export const APPEND_MONEYFLOW_TRACKER = 'APPEND_MONEYFLOW_TRACKER' as const
type AppendMoneyflowTracker = PayloadAction<typeof APPEND_MONEYFLOW_TRACKER, MoneyflowTrackerPayload<MoneyflowTracker>>
export const appendMoneyflowTrackerActionCreator = (type: MoneyflowType, data: MoneyflowTracker): AppendMoneyflowTracker =>
  createPayloadAction(APPEND_MONEYFLOW_TRACKER, { type, data })

export const TOGGLE_MONEYFLOW_TRACKER_DETAILS = 'TOGGLE_MONEYFLOW_TRACKER_DETAILS' as const
type ToggleMoneyflowTrackerDetails = PayloadAction<typeof TOGGLE_MONEYFLOW_TRACKER_DETAILS, TrackerActionPayload>
export const toggleMoneyflowTrackerDetailsActionCreator = (type: MoneyflowType, categoryId: CategoryId): ToggleMoneyflowTrackerDetails =>
  createPayloadAction(TOGGLE_MONEYFLOW_TRACKER_DETAILS, { type, categoryId })

type DeleteMoneyflowTrackerActionPayload = {
  type: MoneyflowType
  tracker: MoneyflowTracker
}
export const DELETE_MONEYFLOW_TRACKER = 'DELETE_MONEYFLOW_TRACKER' as const
type DeleteMoneyflowTracker = RequestAction<typeof DELETE_MONEYFLOW_TRACKER, DeleteMoneyflowTrackerActionPayload>
export const deleteMoneyflowTrackerActionCreator =
  createRequestActionCreators<typeof DELETE_MONEYFLOW_TRACKER, DeleteMoneyflowTrackerActionPayload>(DELETE_MONEYFLOW_TRACKER)

// ===========================================================================//
//                                   TRANSACTIONS                             //
// ===========================================================================//

export const GET_TRANSACTIONS = 'GET_TRANSACTIONS' as const
type GetTransactions = PayloadAction<typeof GET_TRANSACTIONS, BaseTransactionsPayload>
export const getTransactionsActionCreator = (payload: BaseTransactionsPayload): GetTransactions =>
  createPayloadAction(GET_TRANSACTIONS, payload)

export const SET_TRANSACTIONS = 'SET_TRANSACTIONS' as const
type SetTransactions = PayloadAction<typeof SET_TRANSACTIONS, TransactionsPayload>
export const setTransactionsActionCreator = (payload: TransactionsPayload): SetTransactions =>
  createPayloadAction(SET_TRANSACTIONS, payload)

export const SET_TRANSACTION = 'SET_TRANSACTION' as const
type SetTransaction = PayloadAction<typeof SET_TRANSACTION, { type: FullMoneyflowType, transaction: Transaction }>
export const setTransactionActionCreator = (type: FullMoneyflowType, transaction: Transaction): SetTransaction =>
  createPayloadAction(SET_TRANSACTION, { type, transaction })

export const TOGGLE_TRANSACTIONS_LOADING = 'TOGGLE_TRANSACTIONS_LOADING' as const
type ToggleTransactionsLoading = PayloadAction<typeof TOGGLE_TRANSACTIONS_LOADING, { type: FullMoneyflowType, toggleValue: boolean }>
export const toggleTransactionsLoadingActionCreator = (type: FullMoneyflowType, toggleValue: boolean): ToggleTransactionsLoading =>
  createPayloadAction(TOGGLE_TRANSACTIONS_LOADING, { type, toggleValue })

export const CHANGE_TRANSACTION_CATEGORY = 'CHANGE_TRANSACTION_CATEGORY' as const
export type ChangeTransactionCategory = RequestAction<typeof CHANGE_TRANSACTION_CATEGORY, TransactionPayload, Transaction>
export const changeTransactionCategoryActionCreator =
  createRequestActionCreators<typeof CHANGE_TRANSACTION_CATEGORY, TransactionPayload, Transaction>(CHANGE_TRANSACTION_CATEGORY)

export const TOGGLE_TRANSACTION_EDIT = 'TOGGLE_TRANSACTION_EDIT' as const
type ToggleTransactionEdit = PayloadAction<typeof TOGGLE_TRANSACTION_EDIT, { type: MoneyflowType, uid: TransactionUid, toggleValue: boolean }>
export const toggleTransactionEditActionCreator = (type: FullMoneyflowType, uid: TransactionUid, toggleValue: boolean) =>
  createPayloadAction(TOGGLE_TRANSACTION_EDIT, { type, uid, toggleValue })

type GetRecentTransactionsRequest = {
}
type GetRecentTransactionsResponse = {
  transactions: Collection<Transaction, 'uid'>
}
export const GET_RECENT_TRANSACTIONS = 'GET_RECENT_TRANSACTIONS' as const
type GetRecentTransactions = RequestAction<typeof GET_RECENT_TRANSACTIONS, GetRecentTransactionsRequest, GetRecentTransactionsResponse>
export const getRecentTransactionsActionCreator =
  createRequestActionCreators<typeof GET_RECENT_TRANSACTIONS, GetRecentTransactionsRequest, GetRecentTransactionsResponse>(GET_RECENT_TRANSACTIONS)

type GetSimilarTransactionsRequest = {
  targetTransactionId: TransactionUid
}
type GetSimilarTransactionsResponse = Collection<Transaction, 'uid'>
export const GET_SIMILAR_TRANSACTIONS = 'GET_SIMILAR_TRANSACTIONS' as const
type GetSimilarTransactions = RequestAction<typeof GET_SIMILAR_TRANSACTIONS, GetSimilarTransactionsRequest, GetSimilarTransactionsResponse>
export const getSimilarTransactionsActionCreator =
  createRequestActionCreators<typeof GET_SIMILAR_TRANSACTIONS, GetSimilarTransactionsRequest, GetSimilarTransactionsResponse>(GET_SIMILAR_TRANSACTIONS)

export type MoneyflowActions =
  | GetMemberMoneyflowBreakdown
  | SetMemberMoneyflowBreakdown
  | GetPeergroupMoneyflowBreakdown
  | AppendPeergroupMoneyflowBreakdown
  | SetBreakdownVisibility
  | GetMemberMoneyflowTrend
  | SetMemberMoneyflowTrend
  | GetPeergroupMoneyflowTrend
  | AppendPeergroupMoneyflowTrend
  | SetCategoriesVisibility
  | GetMoneyflowTrackers
  | DeleteMoneyflowTracker
  | GetTransactions
  | SetTransactions
  | SetTransaction
  | ChangeTransactionCategory
  | ToggleMoneyflowTrackerDetails
  | ToggleTransactionsLoading
  | AppendMoneyflowTracker
  | ToggleTransactionEdit
  | GetRecentTransactions
  | GetSimilarTransactions

export default MoneyflowActions
